"use client";

import { FieldControl, FieldLabel, FieldRoot } from "@tillix/ui-atoms/field";
import { Input } from "@tillix/ui-atoms/input";
import { Radio, RadioGroup } from "@tillix/ui-atoms/radio-group";
import { useAppointmentT } from "../i18n/use-t";
import type { ContactInput } from "../lib/contact";

interface ContactCaptureProps {
  contact: ContactInput;
  onChange: (contact: ContactInput) => void;
}

/**
 * Email-or-phone capture shown before a reschedule/cancel when the SAP order
 * carries no contact info, so the confirmation nudge still has a recipient.
 * Fully controlled; the parent gates its submit on `isCompleteContact`.
 */
export default function ContactCapture({
  contact,
  onChange,
}: ContactCaptureProps) {
  const t = useAppointmentT();
  const isEmail = contact.channel === "email";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <p className="font-medium text-sm">{t("contact.title")}</p>
        <p className="text-muted-foreground text-sm">
          {t("contact.description")}
        </p>
      </div>
      <RadioGroup
        className="flex flex-row gap-4"
        onValueChange={(value) =>
          // Switching channels clears the input — an email is never a valid
          // phone number and vice versa.
          onChange({
            channel: String(value) === "phone" ? "phone" : "email",
            value: "",
          })
        }
        value={contact.channel}
      >
        {/* biome-ignore lint/a11y/noLabelWithoutControl: Radio is the labeled control; Base UI renders it as an input */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Radio value="email" />
          {t("contact.emailOption")}
        </label>
        {/* biome-ignore lint/a11y/noLabelWithoutControl: Radio is the labeled control; Base UI renders it as an input */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Radio value="phone" />
          {t("contact.phoneOption")}
        </label>
      </RadioGroup>
      <FieldRoot name="nudgeContact">
        <FieldLabel className="sr-only">
          {isEmail ? t("contact.emailLabel") : t("contact.phoneLabel")}
        </FieldLabel>
        <FieldControl
          render={(defaultProps) => (
            <Input
              {...defaultProps}
              inputMode={isEmail ? "email" : "tel"}
              onChange={(e) =>
                onChange({ channel: contact.channel, value: e.target.value })
              }
              placeholder={
                isEmail
                  ? t("contact.emailPlaceholder")
                  : t("contact.phonePlaceholder")
              }
              type={isEmail ? "email" : "tel"}
              value={contact.value}
            />
          )}
        />
      </FieldRoot>
    </div>
  );
}
